/**
 * Credits Context & Provider
 * 
 * Provides credit balance and operations throughout the React app.
 * Uses Supabase Realtime for instant balance updates.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import creditsService, { type UserCredits, type CreditTransaction, type UserSubscription } from '../services/CreditsService';
import { initializePricing, isPricingInitialized } from '../utils/creditCostUtils';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface CreditsContextType {
  balance: number;
  credits: UserCredits | null;
  subscription: UserSubscription | null;
  /**
   * Single source of truth for plan-based UI gating.
   * Resolves to the active subscription's plan slug, or 'free' when the user
   * has no subscription. New slugs: 'free' | 'basic' | 'pro' | 'corporate'.
   */
  tier: string;
  loading: boolean;
  error: string | null;
  recentTransactions: CreditTransaction[];
  refreshBalance: () => Promise<void>;
  getHistory: (limit?: number) => Promise<CreditTransaction[]>;
  calculateCost: (provider: string, modelId: string, units?: number) => Promise<number>;
  hasEnoughCredits: (cost: number) => boolean;
  deductOptimistically: (amount: number) => void;
  refundOptimistically: (amount: number) => void;
}

const CreditsContext = createContext<CreditsContextType | null>(null);

export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<CreditTransaction[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const transactionsChannelRef = useRef<RealtimeChannel | null>(null);

  const refreshBalance = useCallback(async (retryCount = 0, maxRetries = 4) => {
    if (!user?.id) {
      setCredits(null);
      return;
    }

    // Only show loading on first attempt
    if (retryCount === 0) {
      setLoading(true);
      setError(null);
    }

    try {
      const userCredits = await creditsService.getBalance(user.id);
      setCredits(userCredits);
      setError(null);
      if (retryCount > 0) {
        console.log(`[CreditsContext] ✅ Balance fetched successfully after ${retryCount} retries`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch credits';
      console.error(`[CreditsContext] Failed to fetch balance (attempt ${retryCount + 1}/${maxRetries + 1}):`, err);
      
      // Retry with exponential backoff if we haven't reached max retries
      if (retryCount < maxRetries) {
        const delayMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s, 8s
        console.log(`[CreditsContext] Retrying in ${delayMs}ms...`);
        
        setTimeout(() => {
          refreshBalance(retryCount + 1, maxRetries);
        }, delayMs);
      } else {
        // All retries exhausted
        console.error('[CreditsContext] ❌ All retry attempts exhausted');
        setError(errorMessage);
        // Don't clear credits on error - keep stale data
      }
    } finally {
      // Only clear loading on first attempt or final failure
      if (retryCount === 0 || retryCount >= maxRetries) {
        setLoading(false);
      }
    }
  }, [user?.id]);

  const getHistory = useCallback(async (limit = 50): Promise<CreditTransaction[]> => {
    if (!user?.id) {
      return [];
    }

    try {
      return await creditsService.getHistory(user.id, limit);
    } catch (err) {
      console.error('[CreditsContext] Failed to fetch history:', err);
      throw err;
    }
  }, [user?.id]);

  const calculateCost = useCallback(async (
    provider: string, 
    modelId: string, 
    units = 1
  ): Promise<number> => {
    try {
      const result = await creditsService.calculateCost(provider, modelId, units);
      return result.totalCost;
    } catch (err) {
      console.error('[CreditsContext] Failed to calculate cost:', err);
      throw err;
    }
  }, []);

  const hasEnoughCredits = useCallback((cost: number): boolean => {
    if (!credits) return false;
    return credits.balance >= cost;
  }, [credits]);

  /**
   * Optimistically deduct credits from the UI immediately
   * This provides instant feedback before server confirms
   */
  const deductOptimistically = useCallback((amount: number) => {
    setCredits(prev => {
      if (!prev) return prev;
      console.log(`[CreditsContext] Optimistic deduction: ${amount} credits (${prev.balance} -> ${prev.balance - amount})`);
      return {
        ...prev,
        balance: prev.balance - amount
      };
    });
  }, []);

  /**
   * Optimistically refund credits to the UI
   * Called when a generation fails after optimistic deduction
   */
  const refundOptimistically = useCallback((amount: number) => {
    setCredits(prev => {
      if (!prev) return prev;
      console.log(`[CreditsContext] Optimistic refund: ${amount} credits (${prev.balance} -> ${prev.balance + amount})`);
      return {
        ...prev,
        balance: prev.balance + amount
      };
    });
  }, []);

  // Fetch balance and pricing when user changes
  useEffect(() => {
    if (user?.id) {
      refreshBalance();
      // Load model pricing from DB (once per session)
      if (!isPricingInitialized()) {
        creditsService.getPricing()
          .then(models => initializePricing(models))
          .catch(err => console.warn('[CreditsContext] Failed to load pricing from DB, using fallbacks:', err));
      }
    } else {
      setCredits(null);
    }
  }, [user?.id, refreshBalance]);

  // Subscribe to Supabase Realtime for instant balance updates
  useEffect(() => {
    if (!user?.id) {
      // Cleanup subscription if user logs out
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    // Create realtime subscription for user_credits table
    const channel = supabase
      .channel(`user_credits:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[CreditsContext] Realtime update received:', payload.new);
          // Update credits with new balance from realtime
          setCredits(prev => {
            // If prev is null, create new object instead of spreading null
            if (!prev) {
              return {
                userId: payload.new.user_id,
                balance: payload.new.balance,
                lifetimeUsed: payload.new.lifetime_used,
                lifetimePurchased: payload.new.lifetime_purchased,
                lifetimeRefunded: payload.new.lifetime_refunded,
                createdAt: payload.new.created_at,
                updatedAt: payload.new.updated_at,
              } as UserCredits;
            }
            // Normal update - preserve all existing fields
            return {
              ...prev,
              balance: payload.new.balance,
              lifetimeUsed: payload.new.lifetime_used,
              lifetimePurchased: payload.new.lifetime_purchased,
              lifetimeRefunded: payload.new.lifetime_refunded,
              updatedAt: payload.new.updated_at,
            } as UserCredits;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[CreditsContext] New credits record:', payload.new);
          setCredits({
            userId: payload.new.user_id,
            balance: payload.new.balance,
            lifetimeUsed: payload.new.lifetime_used,
            lifetimePurchased: payload.new.lifetime_purchased,
            lifetimeRefunded: payload.new.lifetime_refunded,
            createdAt: payload.new.created_at,
            updatedAt: payload.new.updated_at,
          } as UserCredits);
        }
      )
      .subscribe((status) => {
        console.log('[CreditsContext] Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('[CreditsContext] ✅ Realtime connected - instant balance updates enabled');
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('[CreditsContext] ⚠️ Realtime connection error - falling back to polling');
          // Start fallback polling if realtime fails
          startFallbackPolling();
        }
      });

    channelRef.current = channel;

    // Cleanup on unmount or user change
    return () => {
      if (channelRef.current) {
        console.log('[CreditsContext] Cleaning up realtime subscription');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id]);

  // Subscribe to credit_transactions for recent activity updates
  useEffect(() => {
    if (!user?.id) {
      if (transactionsChannelRef.current) {
        supabase.removeChannel(transactionsChannelRef.current);
        transactionsChannelRef.current = null;
      }
      setRecentTransactions([]);
      return;
    }

    // Load initial transactions (fetch more to apply date filtering)
    getHistory(100).then(setRecentTransactions).catch(err => {
      console.error('[CreditsContext] Failed to load transactions:', err);
    });

    // Subscribe to new transactions
    const txChannel = supabase
      .channel(`credit_transactions:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[CreditsContext] New transaction:', payload.new);
          const newTx: CreditTransaction = {
            id: payload.new.id,
            amount: payload.new.amount,
            type: payload.new.type,
            status: payload.new.status,
            description: payload.new.description,
            provider: payload.new.provider,
            modelId: payload.new.model_id,
            operationType: payload.new.operation_type,
            createdAt: payload.new.created_at,
          };
          // Add to front, keep only 20
          setRecentTransactions(prev => [newTx, ...prev].slice(0, 20));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'credit_transactions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          // Update existing transaction (e.g., status change from pending to completed)
          setRecentTransactions(prev => 
            prev.map(tx => tx.id === payload.new.id ? {
              ...tx,
              status: payload.new.status,
              amount: payload.new.amount,
            } : tx)
          );
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[CreditsContext] ✅ Transactions realtime connected');
        }
      });

    transactionsChannelRef.current = txChannel;

    return () => {
      if (transactionsChannelRef.current) {
        supabase.removeChannel(transactionsChannelRef.current);
        transactionsChannelRef.current = null;
      }
    };
  }, [user?.id, getHistory]);

  // Fallback polling function (only used if realtime fails)
  const fallbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const startFallbackPolling = useCallback(() => {
    if (fallbackIntervalRef.current) return; // Already polling
    
    console.log('[CreditsContext] Starting fallback polling (every 30s)');
    fallbackIntervalRef.current = setInterval(() => {
      refreshBalance();
    }, 30000);
  }, [refreshBalance]);

  // Cleanup fallback polling on unmount
  useEffect(() => {
    return () => {
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };
  }, []);

  const value: CreditsContextType = {
    balance: credits?.balance ?? 0,
    credits,
    subscription: credits?.subscription ?? null,
    tier: credits?.subscription?.planSlug ?? 'free',
    loading,
    error,
    recentTransactions,
    refreshBalance,
    getHistory,
    calculateCost,
    hasEnoughCredits,
    deductOptimistically,
    refundOptimistically,
  };

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

/**
 * Hook to access credits context
 */
export function useCredits(): CreditsContextType {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}
